import { useState } from 'react';
import { useGT } from 'gt-react';
import type { AgentScheduleResponse } from '@erp/rpc';
import { toast } from 'src/components/ui/toast';
import {
  useCreateAgentSchedule,
  useDeleteAgentSchedule,
  useUpdateAgentSchedule,
} from 'src/api/rpc/hooks/agent-schedules/useAgentSchedules';
import {
  agentScheduleDraftToInput,
  type AgentScheduleDraft,
} from '../agentScheduleDraft';

/** Server error message when the mutation surfaced one, else the fallback. */
function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function fail(error: unknown, fallback: string) {
  toast.error(mutationErrorMessage(error, fallback));
}

/**
 * The save / toggle / delete actions behind the schedules section, each
 * wrapped in the toast feedback the section shows. Keeps the section itself
 * to layout and dialog state.
 */
export function useAgentScheduleActions(agentDefinitionId: string) {
  const gt = useGT();
  const create = useCreateAgentSchedule();
  const update = useUpdateAgentSchedule();
  const remove = useDeleteAgentSchedule();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  /** Create or update from a draft; resolves true when the save went through. */
  async function save(draft: AgentScheduleDraft): Promise<boolean> {
    const input = agentScheduleDraftToInput(draft);
    try {
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, ...input });
        toast.success(
          gt('Schedule updated', {
            context: 'toast after editing an agent run schedule',
          }),
        );
      } else {
        await create.mutateAsync({ agentDefinitionId, ...input });
        toast.success(
          gt('Schedule created', {
            context: 'toast after creating an agent run schedule',
          }),
        );
      }
      return true;
    } catch (error) {
      fail(
        error,
        gt('Failed to save the schedule', {
          context: 'toast when saving an agent run schedule failed',
        }),
      );
      return false;
    }
  }

  async function toggle(schedule: AgentScheduleResponse, isEnabled: boolean) {
    setTogglingId(schedule.id);
    try {
      await update.mutateAsync({ id: schedule.id, isEnabled });
    } catch (error) {
      fail(
        error,
        gt('Failed to update the schedule', {
          context: 'toast when toggling an agent run schedule failed',
        }),
      );
    }
    setTogglingId(null);
  }

  /** Resolves true when the schedule is gone. */
  async function destroy(schedule: AgentScheduleResponse): Promise<boolean> {
    try {
      await remove.mutateAsync({ id: schedule.id });
      toast.success(
        gt('Schedule deleted', {
          context: 'toast after deleting an agent run schedule',
        }),
      );
      return true;
    } catch (error) {
      fail(
        error,
        gt('Failed to delete the schedule', {
          context: 'toast when deleting an agent run schedule failed',
        }),
      );
      return false;
    }
  }

  return {
    save,
    toggle,
    destroy,
    togglingId,
    isSaving: create.isPending || update.isPending,
    isDeleting: remove.isPending,
  };
}
